import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import useHeaders from "../../../hooks/useHeaders";
import { NavbarHotel } from "../NavbarHotel";
import { NavbarMobileHotel } from "../NavbarMobileHotel";
import { ShowDateToUser } from "../../../components/ShowDateToUser";
import { Paginado } from "../../../components/Paginado";
import { useAppSelector } from "../../../hooks/hooks";
import { ModalCrearLimpieza } from "./ModalCrearLimpieza";
import { ModalGestionarLimpieza } from "./ModalGestionarLimpieza";
import { ModalSeleccionarHabitacion } from "./ModalSeleccionarHabitacion";
import Swal from "sweetalert2";

// --- Interfaces ---
interface Habitacion {
    id: number;
    nombre: string;
}

interface UsuarioLimpieza {
    id: number;
    nombre: string;
    apellido: string;
    celular: string;
}

export interface Limpieza {
    id: number;
    id_habitacion: number;
    estado: "Pendiente" | "En Proceso" | "Terminado";
    fecha_inicio: string | null;
    fecha_fin: string | null;
    notas: string;
    habitacion: Habitacion;
    usuario?: UsuarioLimpieza;
    createdAt: string;
}

export const LimpiezasHotel = () => {
    const { headers } = useHeaders();

    const house = useAppSelector((state) => state.landing_house.house);
    const { id: miIdUsuario, tipo_usuario } = useAppSelector(
        (state) => state.usuario_logeado,
    );

    // Estados para datos
    const [limpiezas, setLimpiezas] = useState<Limpieza[]>([]);
    const [loading, setLoading] = useState(false);

    // Estados para filtros
    const [estadoFiltro, setEstadoFiltro] = useState<string>("Todos");
    const [idHabitacion, setIdHabitacion] = useState<string>("");
    const [modalFiltroOpen, setModalFiltroOpen] = useState(false);

    const [habitacionSeleccionada, setHabitacionSeleccionada] =
        useState<Habitacion | null>(null);
    const [modalHabitaciones, setModalHabitaciones] = useState(false);

    // Paginación (ajustar según tu backend si implementaste count)
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [modalCrear, setModalCrear] = useState(false);
    const LIMPIEZAS_PER_PAGE = 20;

    const [limpiezaSelect, setLimpiezaSelect] = useState<Limpieza | null>(null);
    const [modalGestion, setModalGestion] = useState(false);

    const fetchLimpiezas = async () => {
        setLoading(true);
        try {
            // Creamos un objeto con los filtros
            const queryParams: any = {};

            // 1. Si el estado NO es "Todos", lo agregamos
            if (estadoFiltro !== "Todos") {
                queryParams.estado = estadoFiltro;
            }

            // 2. Si hay algo escrito en el buscador de habitación, lo agregamos
            if (habitacionSeleccionada) {
                queryParams.id_habitacion = habitacionSeleccionada.id;
            }

            // 3. El hotel siempre se manda (si existe)
            if (house) {
                queryParams.id_hotel = house.id;
            }

            // 4. NUEVO: FILTRO ESTRICTO PARA TIPO 10 (Personal de Limpieza)
            if (tipo_usuario === 10) {
                queryParams.id_usuario_limpieza = miIdUsuario;
            }

            const { data } = await axios.get(
                `${process.env.REACT_APP_PROXY}/limpieza_habitaciones`,
                {
                    headers,
                    params: queryParams, // Axios se encarga de convertirlos a req.query
                },
            );

            setLimpiezas(data);
        } catch (error) {
            console.error("Error al obtener limpiezas", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendWhatsApp = (l: Limpieza) => {
        if (!l.usuario) {
            Swal.fire(
                "Atención",
                "No hay personal asignado a esta limpieza para enviarle el mensaje.",
                "warning",
            );
            return;
        }

        // Asegúrate de que el backend esté devolviendo el 'celular' en el include del usuario
        const celular = l.usuario.celular;
        if (!celular) {
            Swal.fire(
                "Atención",
                "El empleado asignado no tiene un número de celular registrado.",
                "warning",
            );
            return;
        }

        const numeroLimpio = celular.replace(/\s+/g, "");

        const enlaceMagico = `${window.location.origin}/limpieza-express/${l.id}`;

        const mensaje = `¡Hola ${l.usuario.nombre}! Tienes una nueva tarea de limpieza asignada.\n\n*Hotel/Casa:* ${house?.nombre}\n*Habitación:* ${l.habitacion?.nombre}\n\nIngresa a este enlace para marcar el inicio y fin de tu limpieza:\n\n\n${enlaceMagico}`;

        const url = `https://wa.me/${numeroLimpio}?text=${encodeURIComponent(mensaje)}`;
        window.open(url, "_blank");
    };

    useEffect(() => {
        fetchLimpiezas();
    }, [estadoFiltro, idHabitacion, house.id, habitacionSeleccionada]);

    const renderHeader = () => (
        <div className="flex items-center px-3 mx-auto mt-12 sm:px-6">
            {modalCrear && (
                <ModalCrearLimpieza
                    modalVisible={modalCrear}
                    setModalVisible={setModalCrear}
                    fetchLimpiezas={fetchLimpiezas}
                />
            )}

            {modalGestion && (
                <ModalGestionarLimpieza
                    limpieza={limpiezaSelect as Limpieza}
                    modalVisible={modalGestion}
                    setModalVisible={setModalGestion}
                    refresh={fetchLimpiezas}
                />
            )}

            {modalHabitaciones && (
                <ModalSeleccionarHabitacion
                    modalVisible={modalHabitaciones}
                    setModalVisible={setModalHabitaciones}
                    idHotel={house.id}
                    onSelect={(h: any) => setHabitacionSeleccionada(h)}
                />
            )}
            <div className="flex flex-col items-center justify-center w-full gap-3 md818:flex-row md818:justify-start">
                <div className="w-full flex items-center justify-between">
                    <div className="flex justify-center items-center gap-2">
                        <div className="relative">
                            <button
                                onClick={() =>
                                    setModalFiltroOpen(!modalFiltroOpen)
                                }
                                className="flex items-center gap-2 px-4 py-2 border-2 border-[#CCCCCC] rounded-lg text-[#808080] hover:bg-gray-50 h-[51px]"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                                    />
                                </svg>
                                Estado: {estadoFiltro}
                            </button>

                            {modalFiltroOpen && (
                                <div className="absolute z-20 w-48 py-2 mt-2 bg-white border rounded-lg shadow-xl">
                                    {[
                                        "Todos",
                                        "Pendiente",
                                        "En Proceso",
                                        "Terminado",
                                    ].map((opcion) => (
                                        <div
                                            key={opcion}
                                            onClick={() => {
                                                setEstadoFiltro(opcion);
                                                setModalFiltroOpen(false);
                                            }}
                                            className="px-4 py-2 cursor-pointer hover:bg-blue-500 hover:text-white"
                                        >
                                            {opcion}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={() => setModalHabitaciones(true)}
                                className="flex items-center gap-2 px-4 py-2 border-2 border-[#CCCCCC] rounded-lg text-[#808080] hover:bg-gray-50 h-[51px] min-w-[200px]"
                            >
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                    />
                                </svg>
                                {habitacionSeleccionada ? (
                                    <span className="font-bold text-blue-600 truncate">
                                        {habitacionSeleccionada.nombre}
                                    </span>
                                ) : (
                                    "Filtrar por Habitación"
                                )}
                            </button>

                            {/* Botón X para deseleccionar rápido (solo si hay una seleccionada) */}
                            {habitacionSeleccionada && (
                                <button
                                    onClick={() =>
                                        setHabitacionSeleccionada(null)
                                    }
                                    className="h-[51px] px-3 text-red-500 border-2 border-red-200 rounded-lg hover:bg-red-50"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                    {tipo_usuario !== 10 && (
                        <button
                            onClick={() => setModalCrear(true)}
                            className="flex items-center gap-1 p-2 border-2 rounded-lg h-[51px] border-blue-600 text-blue-600 hover:bg-blue-50 transition-all"
                        >
                            <svg
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            <span>Nueva limpieza</span>
                        </button>
                    )}
                </div>

                {/* Buscador por ID de Habitación o Nombre (Input simplificado) */}
            </div>
        </div>
    );

    const formatFullDate = (dateString: string) => {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat("es-ES", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        }).format(date);
    };

    const renderTable = () => (
        <div className="w-full px-11">
            <table className="w-full mt-8 overflow-x-auto">
                <thead className="text-left border-b-[3px] border-[#CCCCCC] text-sm uppercase">
                    <tr>
                        <th className="pb-4">Habitación</th>
                        <th className="pb-4 text-center">Estado</th>
                        {tipo_usuario !== 10 && (
                            <th className="pb-4 text-center">Personal</th>
                        )}
                        <th className="pb-4 text-center">Inicio</th>
                        <th className="pb-4 text-center">Fin</th>
                        <th className="pb-4 text-center">Gestionar</th>
                    </tr>
                </thead>
                <tbody>
                    {limpiezas.map((l) => (
                        <tr
                            key={l.id}
                            className="text-sm border-b hover:bg-gray-50"
                        >
                            <td className="py-4 font-bold border-r-[3px] border-[#CCCCCC]">
                                {l.habitacion?.nombre}
                            </td>
                            <td className="text-center border-r-[3px] border-[#CCCCCC]">
                                <span
                                    className={`px-2 py-1 rounded-full text-xs ${
                                        l.estado === "Pendiente"
                                            ? "bg-red-100 text-red-600"
                                            : l.estado === "En Proceso"
                                              ? "bg-yellow-100 text-yellow-600"
                                              : "bg-green-100 text-green-600"
                                    }`}
                                >
                                    {l.estado}
                                </span>
                            </td>
                            {tipo_usuario !== 10 && (
                                <td className="text-center border-r-[3px] border-[#CCCCCC]">
                                    {l.usuario
                                        ? `${l.usuario.nombre} ${l.usuario.apellido}`
                                        : "Sin asignar"}
                                </td>
                            )}
                            <td className="text-center border-r-[3px] border-[#CCCCCC] text-xs 2xl:text-sm">
                                {l.fecha_inicio ? (
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-stone-700">
                                            {
                                                formatFullDate(
                                                    l.fecha_inicio,
                                                ).split(",")[0]
                                            }{" "}
                                            {/* Fecha */}
                                        </span>
                                        <span className="text-blue-500 font-bold">
                                            {new Date(
                                                l.fecha_inicio,
                                            ).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}{" "}
                                            {/* Hora */}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-gray-400">--:--</span>
                                )}
                            </td>
                            <td className="text-center border-r-[3px] border-[#CCCCCC] text-xs 2xl:text-sm">
                                {l.fecha_fin ? (
                                    <div className="flex flex-col">
                                        <span className="font-semibold text-stone-700">
                                            {
                                                formatFullDate(
                                                    l.fecha_fin,
                                                ).split(",")[0]
                                            }{" "}
                                            {/* Fecha */}
                                        </span>
                                        <span className="text-blue-500 font-bold">
                                            {new Date(
                                                l.fecha_fin,
                                            ).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}{" "}
                                            {/* Hora */}
                                        </span>
                                    </div>
                                ) : (
                                    <span className="text-gray-400">--:--</span>
                                )}
                            </td>
                            <td className="text-center px-2">
                                <div className="flex items-center justify-center gap-2">
                                    <button
                                        onClick={() => {
                                            setLimpiezaSelect(l);
                                            setModalGestion(true);
                                        }}
                                        className="px-3 py-1.5 text-xs font-bold text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors"
                                    >
                                        Gestionar
                                    </button>

                                    {/* Botón de WhatsApp (Solo visible para admins y si la tarea no está terminada) */}
                                    {tipo_usuario !== 10 &&
                                        l.estado !== "Terminado" && (
                                            <button
                                                onClick={() =>
                                                    handleSendWhatsApp(l)
                                                }
                                                title="Enviar recordatorio por WhatsApp"
                                                className="flex items-center justify-center w-8 h-8 text-white transition-transform bg-green-500 rounded-md hover:bg-green-600 hover:scale-105"
                                            >
                                                <svg
                                                    className="w-5 h-5"
                                                    fill="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                                                </svg>
                                            </button>
                                        )}
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {limpiezas.length === 0 && !loading && (
                <div className="py-10 text-center text-gray-500">
                    No hay registros de limpieza
                </div>
            )}
        </div>
    );

    return (
        <NavbarHotel titulo="Gestión de Limpieza">
            <div className="w-full">
                {renderHeader()}
                <section className="flex flex-col items-center justify-center lg:py-[10px]">
                    {renderTable()}
                    <div className="mt-4">
                        <Paginado
                            elementsPerPage={LIMPIEZAS_PER_PAGE}
                            cantTotalElements={limpiezas.length} // Cambiar por total del back si existe
                            pagination={(n) => setPageNumber(n)}
                            actualPage={pageNumber}
                        />
                    </div>
                </section>
            </div>
            <NavbarMobileHotel />
        </NavbarHotel>
    );
};
